// MD5 constants
const K = [
  0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee,
  0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
  0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be,
  0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
  0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa,
  0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
  0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed,
  0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
  0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c,
  0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
  0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05,
  0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
  0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039,
  0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
  0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1,
  0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391
];

const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21
];

// Helper functions
const leftRotate = (value, shift) => {
  return (value << shift) | (value >>> (32 - shift));
};

const toHexString = (num) => {
  let hex = '';
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 4; i++) {
    const byte = (num >>> (i * 8)) & 0xff;
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
};

/**
 * Generates an MD5 checksum from file data
 * @param {Uint8Array} data - The file data
 * @returns {Promise<string>} Base64 encoded MD5 hash
 */
const generateMD5Checksum = async (data) => {
  // Convert Uint8Array to string for processing
  const message = Array.from(data).map((byte) => String.fromCharCode(byte)).join('');

  // Initialize variables
  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  // Pre-processing: append padding
  const msgLength = message.length * 8;
  let paddedMessage = message + '\x80';

  // Append zeros until length is 448 mod 512
  while ((paddedMessage.length * 8 + 64) % 512 !== 0) {
    paddedMessage += '\x00';
  }

  // Append original length
  paddedMessage += String.fromCharCode(
    msgLength & 0xff,
    (msgLength >>> 8) & 0xff,
    (msgLength >>> 16) & 0xff,
    (msgLength >>> 24) & 0xff,
    0, 0, 0, 0
  );

  // Process each 512-bit chunk
  for (let i = 0; i < paddedMessage.length; i += 64) {
    const chunk = paddedMessage.slice(i, i + 64);
    const M = [];

    // Break chunk into 16 32-bit words
    // eslint-disable-next-line no-plusplus
    for (let j = 0; j < 16; j++) {
      const offset = j * 4;
      M[j] = (
        chunk.charCodeAt(offset)
        | (chunk.charCodeAt(offset + 1) << 8)
        | (chunk.charCodeAt(offset + 2) << 16)
        | (chunk.charCodeAt(offset + 3) << 24)
      ) >>> 0;
    }

    const AA = a;
    const BB = b;
    const CC = c;
    const DD = d;

    // Main loop
    // eslint-disable-next-line no-plusplus
    for (let j = 0; j < 64; j++) {
      let F; let g;

      if (j < 16) {
        F = (b & c) | (~b & d);
        g = j;
      } else if (j < 32) {
        F = (d & b) | (~d & c);
        g = (5 * j + 1) % 16;
      } else if (j < 48) {
        F = b ^ c ^ d;
        g = (3 * j + 5) % 16;
      } else {
        F = c ^ (b | ~d);
        g = (7 * j) % 16;
      }

      const temp = d;
      d = c;
      c = b;
      b += leftRotate((a + F + K[j] + M[g]) >>> 0, S[j]);
      a = temp;
    }

    a = (a + AA) >>> 0;
    b = (b + BB) >>> 0;
    c = (c + CC) >>> 0;
    d = (d + DD) >>> 0;
  }

  // Convert to hex string
  const hashHex = toHexString(a) + toHexString(b) + toHexString(c) + toHexString(d);

  // Convert hex to base64
  const binaryString = hashHex.match(/.{1,2}/g)?.map((byte) => String.fromCharCode(parseInt(byte, 16))).join('') || '';
  return btoa(binaryString);
};

export default generateMD5Checksum;
